// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { flexRender, type Table as TanstackTable } from "@tanstack/react-table";
import type { MouseEvent } from "react";
import { FilterPopover, type FilterValue } from "@/components/filter-popover";
import { FilterSelect } from "@/components/ui/filter-select";
import { TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { Activity } from "@/hooks/api/use-account-activities";
import { cn } from "@/lib/utils";
import type { CollapsedActivity } from "@/pages/accounts/account-explorer/touchpoints-table/types";
import type { UnifiedTableColumn } from "@/types/table-column";

interface TableHeaderProps {
  table: TanstackTable<Activity | CollapsedActivity>;
  activityColumns: UnifiedTableColumn<Activity | CollapsedActivity>[];
  filters?: Record<string, FilterValue>;
  updateFilter?: (key: string, value: FilterValue) => void;
  clearFilter?: (key: string) => void;
  onColumnResizeDoubleClick?: (e: MouseEvent, columnId: string) => void;
}

/**
 * Renders the fixed header for the activity table with integrated filters
 */
export function ActivityTableHeader({
  table,
  activityColumns,
  filters,
  updateFilter,
  clearFilter,
  onColumnResizeDoubleClick,
}: TableHeaderProps) {
  return (
    <TableHeader className="sticky top-0 z-20">
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id} className="hover:bg-transparent">
          {headerGroup.headers.map((header) => {
            // Find the corresponding activity column to check if it's filterable
            const activityColumn = activityColumns.find(
              (col) => col.key === header.id,
            );

            return (
              <TableHead
                key={header.id}
                className={cn(
                  "relative h-11 text-xs font-normal text-muted-foreground",
                  header.id !== "gutter" &&
                    header.id !== "expand" &&
                    "border-b border-r border-border/50",
                  header.id === "gutter" && "bg-card border-r border-border/50",
                  header.id === "direction" && "px-1",
                )}
              >
                <div className="flex items-center justify-between relative">
                  {/* Render column header */}
                  <div className="truncate mr-1 min-w-0">
                    {header.isPlaceholder || header.id === "direction"
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </div>

                  {/* Render filter based on filter type - ONLY if not loading */}
                  {activityColumn?.filterable &&
                    filters &&
                    updateFilter &&
                    clearFilter &&
                    (activityColumn.filterType === "select" ? (
                      <FilterSelect
                        column={activityColumn as UnifiedTableColumn}
                        currentFilter={
                          (filters[activityColumn.key] || null) as FilterValue
                        }
                        onFilterChange={updateFilter}
                        onClearFilter={clearFilter}
                      />
                    ) : (
                      <FilterPopover
                        column={activityColumn as UnifiedTableColumn}
                        currentFilter={
                          (filters[activityColumn.key] || null) as FilterValue
                        }
                        onFilterChange={updateFilter}
                        onClearFilter={clearFilter}
                        showNullFilter={false}
                      />
                    ))}
                </div>
                {/* Column resize handle */}
                {header.column.getCanResize() && (
                  <div
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    onDoubleClick={(e) =>
                      onColumnResizeDoubleClick?.(e, header.id)
                    }
                    className={cn(
                      "resize-handle",
                      header.column.getIsResizing()
                        ? "table-head-resizing"
                        : "",
                    )}
                    data-resize-handle={header.id}
                    data-near-bottom={
                      // Tag resize handles near the bottom of the table for smart positioning
                      headerGroup.headers.indexOf(header) >
                      headerGroup.headers.length * 0.75
                        ? "true"
                        : undefined
                    }
                    title="Double-click to auto-size"
                  />
                )}
              </TableHead>
            );
          })}
        </TableRow>
      ))}
    </TableHeader>
  );
}
